import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { API } from "@/App";
import { toast } from "sonner";
import {
  Wallet, CreditCard, TrendingDown, CalendarClock, ArrowUpRight, Loader2,
  CheckCircle2, ExternalLink,
} from "lucide-react";
import { extractErrorMessage } from "@/lib/errorMessage";

function formatBRL(v) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
}

function badgeUrgencia(dias) {
  if (dias <= 0) return "bg-red-100 text-red-700";
  if (dias <= 1) return "bg-red-50 text-red-600";
  if (dias <= 3) return "bg-amber-100 text-amber-700";
  return "bg-blue-50 text-blue-700";
}

export default function ResumoFinanceiroDiaWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payItem, setPayItem] = useState(null);
  const [payForm, setPayForm] = useState({ data_pagamento: "", valor_pago: 0 });
  const [paying, setPaying] = useState(false);

  const fetchResumo = async () => {
    try {
      const r = await axios.get(`${API}/financeiro/resumo-dia`);
      setData(r.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResumo();
  }, []);

  const openPayModal = (item) => {
    setPayItem(item);
    setPayForm({
      data_pagamento: new Date().toISOString().split("T")[0],
      valor_pago: item.valor,
    });
  };

  const handlePayConfirm = async () => {
    if (!payItem) return;
    setPaying(true);
    try {
      await axios.post(`${API}/lancamentos/${payItem.id}/baixar`, null, {
        params: {
          data_pagamento: payForm.data_pagamento,
          valor_pago: payForm.valor_pago || payItem.valor,
        },
      });
      toast.success("Pagamento registrado com sucesso");
      setPayItem(null);
      setLoading(true);
      fetchResumo();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao registrar pagamento"));
    } finally {
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-sm" data-testid="widget-resumo-dia-loading">
        <CardContent className="p-6 flex items-center justify-center h-32">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const { a_pagar_hoje, saldo_disponivel_cartoes, despesas_mes, proximas_faturas_7d } = data;

  return (
    <Card className="border-0 shadow-sm" data-testid="widget-resumo-dia">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-heading font-semibold text-luvcorp-navy flex items-center gap-2">
              <Wallet className="h-5 w-5 text-luvcorp-gold" />
              Resumo Financeiro do Dia
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">{new Date(data.data_referencia + "T00:00:00").toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })}</p>
          </div>
          <Link to="/financeiro/cartoes" className="text-xs text-luvcorp-gold hover:underline flex items-center gap-1">
            Ver detalhes <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* A pagar HOJE */}
          <div className="p-4 rounded-lg bg-red-50/60 border border-red-100" data-testid="widget-pagar-hoje">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs uppercase tracking-wide text-red-700 font-medium">A pagar hoje</span>
              <TrendingDown className="h-4 w-4 text-red-500" />
            </div>
            <p className="text-2xl font-bold text-red-700">{formatBRL(a_pagar_hoje.total)}</p>
            <p className="text-xs text-slate-500 mt-1">
              {a_pagar_hoje.qtd === 0 ? "Nenhum vencimento" : `${a_pagar_hoje.qtd} item${a_pagar_hoje.qtd > 1 ? "s" : ""}`}
            </p>
            {a_pagar_hoje.itens && a_pagar_hoje.itens.length > 0 && (
              <div className="mt-3 space-y-1.5 max-h-44 overflow-y-auto pr-1">
                {a_pagar_hoje.itens.map((it) => (
                  <div key={`${it.origem}-${it.id}`} className="flex items-center justify-between gap-2 p-2 rounded bg-white border border-red-100" data-testid={`pagar-item-${it.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-700 truncate" title={it.descricao}>{it.descricao}</p>
                      <p className="text-[10px] text-slate-500 font-mono">{formatBRL(it.valor)}</p>
                    </div>
                    {it.origem === "lancamento" ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-[10px] text-emerald-700 hover:bg-emerald-50"
                        onClick={() => openPayModal(it)}
                        data-testid={`btn-pagar-${it.id}`}
                        title="Marcar como pago"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Pagar
                      </Button>
                    ) : (
                      <Link to="/financeiro/cartoes" title="Ver no módulo de cartões">
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-[10px] text-slate-600" data-testid={`btn-ver-cartao-${it.id}`}>
                          <ExternalLink className="h-3.5 w-3.5 mr-1" /> Fatura
                        </Button>
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Limite disponivel */}
          <div className="p-4 rounded-lg bg-emerald-50/60 border border-emerald-100" data-testid="widget-saldo-cartoes">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs uppercase tracking-wide text-emerald-700 font-medium">Limite disponível</span>
              <CreditCard className="h-4 w-4 text-emerald-600" />
            </div>
            <p className="text-2xl font-bold text-emerald-700">{formatBRL(saldo_disponivel_cartoes.disponivel)}</p>
            <div className="mt-2 space-y-1">
              <Progress value={saldo_disponivel_cartoes.utilizacao_pct} className="h-1.5" />
              <p className="text-[10px] text-slate-500">
                {saldo_disponivel_cartoes.utilizacao_pct}% utilizado · {saldo_disponivel_cartoes.qtd_cartoes} {saldo_disponivel_cartoes.qtd_cartoes === 1 ? "cartão" : "cartões"}
              </p>
            </div>
          </div>

          {/* Despesas do mes */}
          <div className="p-4 rounded-lg bg-slate-50 border border-slate-200" data-testid="widget-despesas-mes">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs uppercase tracking-wide text-slate-700 font-medium">Despesas no mês</span>
              <CalendarClock className="h-4 w-4 text-slate-500" />
            </div>
            <p className="text-2xl font-bold text-luvcorp-navy">{formatBRL(despesas_mes.total)}</p>
            <p className="text-[11px] text-slate-500 mt-1">
              Cartão: {formatBRL(despesas_mes.cartao)} · Lançamentos: {formatBRL(despesas_mes.lancamentos)}
            </p>
          </div>
        </div>

        {/* Proximas faturas */}
        {proximas_faturas_7d && proximas_faturas_7d.length > 0 && (
          <div className="mt-5 border-t pt-4">
            <h3 className="text-xs uppercase tracking-wide text-slate-600 font-semibold mb-2">Próximas 3 faturas (7 dias)</h3>
            <div className="space-y-2">
              {proximas_faturas_7d.map((f, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded bg-white border" data-testid={`widget-prox-fatura-${i}`}>
                  <div className="flex items-center gap-3">
                    <Badge className={badgeUrgencia(f.dias)}>
                      {f.dias <= 0 ? "HOJE" : f.dias === 1 ? "Amanhã" : `${f.dias}d`}
                    </Badge>
                    <div>
                      <p className="text-sm font-medium text-luvcorp-navy">{f.card_apelido}</p>
                      <p className="text-[10px] text-slate-500">Competência {f.competencia} · Venc {new Date(f.data_vencimento + "T00:00:00").toLocaleDateString("pt-BR")}</p>
                    </div>
                  </div>
                  <p className="font-mono text-sm font-semibold text-red-600">{formatBRL(f.total)}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>

      {/* Pagamento Rapido Modal */}
      <Dialog open={!!payItem} onOpenChange={(o) => { if (!o) setPayItem(null); }}>
        <DialogContent className="max-w-md" data-testid="dialog-pagamento-rapido">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="h-5 w-5" /> Pagamento Rápido
            </DialogTitle>
            <DialogDescription>Registre a baixa do lançamento sem sair do dashboard.</DialogDescription>
          </DialogHeader>
          {payItem && (
            <div className="space-y-3 py-2">
              <div className="p-3 rounded bg-slate-50 border">
                <p className="text-xs text-slate-500">Lançamento</p>
                <p className="font-medium text-luvcorp-navy">{payItem.descricao}</p>
                <p className="text-xs text-slate-500 mt-1">Valor original: <span className="font-mono">{formatBRL(payItem.valor)}</span></p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="pay-data" className="text-xs">Data do pagamento</Label>
                  <Input
                    id="pay-data"
                    type="date"
                    value={payForm.data_pagamento}
                    onChange={(e) => setPayForm({ ...payForm, data_pagamento: e.target.value })}
                    data-testid="input-pay-data"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="pay-valor" className="text-xs">Valor pago (R$)</Label>
                  <Input
                    id="pay-valor"
                    type="number"
                    step="0.01"
                    value={payForm.valor_pago}
                    onChange={(e) => setPayForm({ ...payForm, valor_pago: parseFloat(e.target.value) || 0 })}
                    data-testid="input-pay-valor"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayItem(null)} disabled={paying}>Cancelar</Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={handlePayConfirm}
              disabled={paying || !payForm.data_pagamento || payForm.valor_pago <= 0}
              data-testid="btn-confirm-pagamento"
            >
              {paying ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
              Confirmar Pagamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
